import { buildWaiter } from '@ember/test-waiters';

/**
 * @module beebee-base/lib/utility/ajax
 * @description AJAX request helpers. jQuery-free: POST/PATCH/DELETE go through
 * native `fetch`, and object payloads are serialized with `bbParam` — a faithful
 * port of jQuery 3.7.1 `jQuery.param` (traditional=false). This preserves the
 * exact `application/x-www-form-urlencoded` wire format the API expects
 * (deep-bracket nesting `a[b][]=1`, `encodeURIComponent` + `%20`→`+`), which
 * `URLSearchParams` does NOT reproduce (it stringifies nested objects to
 * "[object Object]" and encodes `' ( ) ! ~ *` differently).
 */

/**
 * Faithful port of jQuery 3.7.1 `jQuery.param(obj)` with `traditional=false`.
 * Byte-identical to the previous jQuery serialization: arrays → `k[]=v`, nested
 * objects → `k[sub]=v`, arrays of objects → `k[0][sub]=v`, null/undefined → `k=`,
 * value coercion via `String()` (NO Date special-casing — matches jQuery), then
 * `encodeURIComponent` with `%20` replaced by `+`.
 * @param {Object} a - payload object
 * @returns {string} url-encoded body
 */
function bbParam(a) {
  const rbracket = /\[\]$/;
  const s = [];
  // Defence-in-depth against cyclic / DOM-backed payloads. A DOM Event leaking
  // into a payload gives `event.view` → `window` → `window.window` → …: the
  // unguarded `for..in` walk below would follow it forever while the key prefix
  // string grows on every level — exhausting the tab's memory before the request
  // is ever sent (a real browser-killer, not theoretical). Three independent caps,
  // each of which alone keeps the walk finite:
  //   1. `seen` — a WeakSet of the current ancestor path (added on descent, removed
  //      on ascent). Catches a direct/indirect cycle (`o.self === o`, DOM parent↔
  //      child) while output stays byte-identical for a plain object that
  //      legitimately appears more than once in *sibling* branches.
  //   2. MAX_DEPTH — bounds the longest chain (and therefore the prefix length).
  //   3. NODE_BUDGET / MAX_OUTPUT — hard ceilings on total container objects visited
  //      and on the serialized byte length. Ancestor-path dedup alone is NOT enough
  //      for the DOM graph: `window` is reachable via thousands of distinct paths, so
  //      it (and everything under it) gets re-walked combinatorially — even with no
  //      single path cycling — emitting so many long deep-bracketed keys that the
  //      final `join` alone overflows V8's max string ("Invalid string length"), long
  //      before the tab OOMs. MAX_OUTPUT is the true discriminator: any real request
  //      body is well under a few MB, while a leaked graph blows past the ceiling in
  //      milliseconds, so the walk aborts fast and the format stays byte-identical for
  //      every realistic payload. NODE_BUDGET additionally bounds a walk that recurses
  //      widely while emitting little. Whichever trips first, the offending branch is
  //      dropped and the rest of the request still goes out instead of hanging.
  const MAX_DEPTH = 100;
  const NODE_BUDGET = 100000;
  const MAX_OUTPUT = 8000000; // ~8 MB of url-encoded body — far beyond any real request
  const seen = new WeakSet();
  let visited = 0;
  let outputLen = 0;
  let aborted = false;
  let warnedOnce = false;
  const warnSkip = prefix => {
    if (!warnedOnce) {
      // Log once: a malformed graph can trip a guard thousands of times.
      warnedOnce = true;
      console.warn('[bbParam] skipped cyclic or over-deep payload branch at', prefix);
    }
  };
  const add = (key, valueOrFn) => {
    // A function-valued param is a jQuery.param quirk we do NOT rely on: real
    // payloads are plain data. Worse, when a non-plain object (Ember record, class
    // instance) leaks into the payload, `for..in` below walks its prototype and
    // hands its native methods here — invoking one standalone (`this` unbound)
    // throws "Illegal invocation" and crashes the whole request. Skip functions
    // instead of calling them: byte-identical for real payloads, no crash.
    if (typeof valueOrFn === 'function') {
      return;
    }
    // A Symbol leaf cannot be url-encoded (`encodeURIComponent(sym)` throws
    // "Cannot convert a Symbol value to a string", same as jQuery.param would).
    // Real payloads never carry symbols; the only source is a leaked host object
    // (a DOM Event's global graph exposes well-known symbols as values). Skip it
    // instead of letting the whole request throw — part of the same defence as the
    // cycle guard below.
    if (typeof valueOrFn === 'symbol') {
      return;
    }
    const value = valueOrFn;
    // Drop keys whose value is `undefined` (accidental/absent frontend data) so we
    // never emit an empty `key=` — that empty param made backends reject the whole
    // request with 400 "Resource Not Found" (e.g. FSE save, calendar /events,
    // maestro refreshAllEvents, ecommerce dashboard). Explicit `null` and '' are
    // KEPT as `key=` so POST/PATCH field-clears still work.
    if (value === undefined) {
      return;
    }
    const piece = encodeURIComponent(key) + '=' + encodeURIComponent(value === null ? '' : value);
    outputLen += piece.length + 1; // +1 for the '&' join separator
    if (outputLen >= MAX_OUTPUT) {
      // Stop before the accumulated body grows large enough to overflow the final
      // `s.join('&')` (V8 caps string length). Latch `aborted` so buildParams stops
      // recursing too, not just this leaf.
      aborted = true;
      warnSkip(key);
      return;
    }
    s.push(piece);
  };
  const buildParams = (prefix, obj, depth) => {
    if (aborted) {
      return;
    }
    const isObject = obj !== null && typeof obj === 'object';
    if (isObject) {
      // Cyclic reference (obj already on the current ancestor path), runaway depth,
      // or total-work budget exceeded: skip this branch instead of recursing
      // forever. See header comment for why all these guards are needed.
      if (seen.has(obj) || depth > MAX_DEPTH || visited >= NODE_BUDGET) {
        warnSkip(prefix);
        return;
      }
      visited += 1;
      seen.add(obj);
    }
    if (Array.isArray(obj)) {
      obj.forEach((v, i) => {
        if (rbracket.test(prefix)) {
          add(prefix, v);
        } else {
          buildParams(prefix + '[' + (v !== null && typeof v === 'object' ? i : '') + ']', v, depth + 1);
        }
      });
    } else if (isObject) {
      for (const name in obj) {
        buildParams(prefix + '[' + name + ']', obj[name], depth + 1);
      }
    } else {
      add(prefix, obj);
    }
    if (isObject) {
      // Leave the ancestor path so a sibling branch may reuse the same object.
      seen.delete(obj);
    }
  };
  for (const prefix in a) {
    buildParams(prefix, a[prefix], 0);
  }
  return s.join('&').replace(/%20/g, '+');
}

/**
 * Global in-flight request counter driving the `body.beebee-loading` class.
 * Replaces the old jQuery `ajaxStart`/`ajaxStop` hooks (core-app.js), which no
 * longer fire now that every request uses native fetch instead of jQuery.ajax.
 * @private
 */
let _inFlight = 0;

/**
 * Test waiter covering every beebeeAjax request.
 *
 * WHY: these helpers use native `fetch`. `@ember/test-helpers`' pending-request
 * accounting only tracks jQuery.ajax and registered test waiters — and jQuery was
 * removed workspace-wide — so without this, `await settled()` (and therefore
 * `await visit()`) resolves while a request is still in flight. Tests then act on
 * or assert against a pre-data DOM: intermittent "element not found", inputs still
 * `disabled`, empty lists. That race is invisible in tests that only assert
 * `assert.dom('div').exists()`, which is why it went unnoticed.
 *
 * Registering here (rather than per call site) covers every helper at once, since
 * both `_fetchRequest` and `beebeeAjaxGet` already bracket their work with
 * `_setLoading`. Waiters are inert outside tests, so production is unaffected.
 */
const _ajaxWaiter = buildWaiter('beebee-base:beebeeAjax');
const _waiterTokens = [];
function _setLoading(active) {
  // Waiter bookkeeping must run BEFORE the DOM guard below: in a non-DOM
  // environment the early return would end-async a token that was never begun
  // (or leak one), leaving settled() permanently blocked.
  if (active) {
    _waiterTokens.push(_ajaxWaiter.beginAsync());
  } else {
    const token = _waiterTokens.pop();
    if (token) {
      _ajaxWaiter.endAsync(token);
    }
  }
  if (typeof document === 'undefined' || !document.body) {
    return;
  }
  if (active) {
    _inFlight += 1;
    document.body.classList.add('beebee-loading');
  } else {
    _inFlight = Math.max(0, _inFlight - 1);
    if (_inFlight === 0) {
      document.body.classList.remove('beebee-loading');
    }
  }
}

/**
 * Crea le opzioni di autenticazione per le richieste AJAX
 * @private
 * @param {string|null} token - Token di autenticazione
 * @returns {Object} Headers con autorizzazione
 */
function _createAuthHeaders(token) {
  return {
    Authorization: token ? `Bearer ${token}` : ''
  };
}

/**
 * Core fetch wrapper preserving the previous jQuery.ajax semantics:
 * - `dataType:'json'` → response parsed as JSON (empty body → null).
 * - 401 → warn + `window.location.reload()` + reject.
 * - other non-2xx → Error('[beebeeAjax] Request failed: <status> <statusText>')
 *   with `err.responseJSON` populated from the parsed body when available.
 * @private
 */
async function _fetchRequest({
  method,
  url,
  token,
  data,
  json = false
}) {
  const headers = _createAuthHeaders(token);
  const init = {
    method,
    headers,
    // withCredentials:false → same-origin credential behavior (jQuery XHR default).
    credentials: 'same-origin',
    cache: 'no-store'
  };

  // POST/PATCH with an object payload: replicate jQuery's default url-encoding,
  // unless `json` is set (explicit application/json sites → JSON.stringify).
  if (data !== null && data !== undefined) {
    if (json) {
      headers['Content-Type'] = 'application/json; charset=UTF-8';
      init.body = typeof data === 'string' ? data : JSON.stringify(data);
    } else {
      headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
      init.body = typeof data === 'string' ? data : bbParam(data);
    }
  }
  _setLoading(true);
  try {
    let response;
    try {
      response = await fetch(url, init);
    } catch (networkErr) {
      const err = new Error(`[beebeeAjax] Request failed: 0 ${networkErr.message || 'network error'}`);
      err.status = 0;
      err.responseJSON = undefined;
      throw err;
    }

    // Parse body as JSON (dataType:'json'); tolerate empty/non-JSON bodies.
    let parsed;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (response.ok) {
      return parsed;
    }
    if (response.status === 401) {
      console.warn('[beebeeAjax] 401 Unauthorized — reloading session');
      window.location.reload();
      throw new Error('Unauthorized (401)');
    }
    const err = new Error(`[beebeeAjax] Request failed: ${response.status} ${response.statusText || 'unknown'}`);
    err.status = response.status;
    err.responseJSON = parsed ?? undefined;
    throw err;
  } finally {
    _setLoading(false);
  }
}

/**
 * Esegue una richiesta DELETE
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @returns {Promise} Native Promise
 * @throws {Error} Se url non è fornito
 */
function beebeeAjaxDelete(url, token = null, data = null) {
  if (!url) {
    throw new Error('Error no url');
  }
  return _fetchRequest({
    method: 'DELETE',
    url,
    token,
    data
  });
}

/**
 * Esegue una richiesta PATCH
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @param {Object|null} data - Dati da inviare
 * @returns {Promise} Native Promise
 * @throws {Error} Se url non è fornito
 */
function beebeeAjaxPatch(url, token = null, data = null) {
  if (!url) {
    throw new Error('Error no url');
  }
  return _fetchRequest({
    method: 'PATCH',
    url,
    token,
    data
  });
}

/**
 * Esegue una richiesta POST
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @param {Object|null} data - Dati da inviare
 * @returns {Promise} Native Promise
 * @throws {Error} Se url non è fornito
 */
function beebeeAjaxPost(url, token = null, data = null) {
  if (!url) {
    throw new Error('Error no url');
  }
  return _fetchRequest({
    method: 'POST',
    url,
    token,
    data
  });
}

/**
 * Esegue una richiesta POST con body JSON (application/json).
 * Sostituisce i `jQuery.ajax({ contentType: 'application/json', data: JSON.stringify(...) })`.
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @param {Object|null} data - Dati da inviare (serializzati con JSON.stringify)
 * @returns {Promise} Native Promise
 * @throws {Error} Se url non è fornito
 */
function beebeeAjaxPostJson(url, token = null, data = null) {
  if (!url) {
    throw new Error('Error no url');
  }
  return _fetchRequest({
    method: 'POST',
    url,
    token,
    data,
    json: true
  });
}

/**
 * Esegue una richiesta GET con fetch API
 * @param {string} url - URL della richiesta
 * @param {string|null} token - Token di autenticazione
 * @param {Object} data - Query parameters
 * @returns {Promise<Object>} Risposta JSON o oggetto errore
 */
async function beebeeAjaxGet(url, token = null, data = {}) {
  if (!url) {
    return {
      success: false,
      message: 'Missing URL'
    };
  }
  const completeURL = new URL(url.startsWith('http') ? url : url, window.location.origin);
  const filteredData = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== null && value !== undefined));
  for (const [key, value] of Object.entries(filteredData)) {
    if (Array.isArray(value)) {
      value.forEach(val => completeURL.searchParams.append(`${key}[]`, val));
    } else {
      completeURL.searchParams.append(key, value);
    }
  }
  completeURL.searchParams.append('_', Date.now());
  _setLoading(true);
  try {
    // GET now REJECTS on error, matching beebeeAjaxPost/Patch/Delete (and the old
    // jQuery.ajax contract). The previous version resolved `{ success:false }` on any
    // failure, which silently killed every caller's `.catch()`/findError branch — a
    // fleet-wide regression from the jQuery removal (see
    // docs/test-analysis/WORKSPACE-COVERAGE-MASTER-PLAN.md §7ter). Callers that guarded only the old
    // resolve-with-{success:false} shape need an explicit try/catch alongside that
    // guard: the guard still covers a 200 whose body encodes a logical failure, but
    // network/HTTP errors now reject and escape uncaught without one.
    // Keep this a CORS "simple request": no custom headers, no credentials mode
    // that forces credentials. `Cache-Control`/`Pragma`/`Expires` and an always-on
    // `Authorization` header are non-simple → they trigger a preflight (OPTIONS)
    // that the cross-origin asset PHP server does not answer, surfacing as a CORS
    // error. Cache busting is already handled by the `_=Date.now()` query param
    // appended above (matches the old jQuery `cache:false` + `withCredentials:false`
    // behavior). Only send `Authorization` when a token is actually present.
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let response;
    try {
      response = await fetch(completeURL, {
        method: 'GET',
        headers,
        cache: 'no-store',
        credentials: 'same-origin'
      });
    } catch (networkErr) {
      const err = new Error(`[beebeeAjax] GET failed: 0 ${networkErr.message || 'network error'}`);
      err.status = 0;
      err.responseJSON = undefined;
      throw err;
    }
    let parsed;
    try {
      const text = await response.text();
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (response.ok) {
      return parsed;
    }
    if (response.status === 401) {
      console.warn('[beebeeAjax] 401 Unauthorized — reloading session');
      window.location.reload();
      throw new Error('Unauthorized (401)');
    }
    const err = new Error(`[beebeeAjax] GET failed: ${response.status} ${response.statusText || 'unknown'}`);
    err.status = response.status;
    err.responseJSON = parsed ?? undefined;
    throw err;
  } finally {
    _setLoading(false);
  }
}

export { bbParam, beebeeAjaxDelete, beebeeAjaxGet, beebeeAjaxPatch, beebeeAjaxPost, beebeeAjaxPostJson };
//# sourceMappingURL=ajax.js.map
